import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { nominatimService } from '../../utils/locationSearch.js';
import type { FormattedSearchResult } from '../../utils/locationSearch.js';
import './LocationSearch.css';

interface LocationSearchProps {
  isOpen: boolean;
  onClose: () => void;
  onLocationSelect: (location: { lat: number; lon: number; displayName: string }) => void;
  placeholder?: string;
  initialQuery?: string;
  className?: string;
}

export const LocationSearch: React.FC<LocationSearchProps> = ({
  isOpen,
  onClose,
  onLocationSelect,
  placeholder = "Ort oder Stadt suchen...",
  initialQuery = "",
  className = ""
}) => {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [searchResults, setSearchResults] = useState<FormattedSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedResultIndex, setSelectedResultIndex] = useState(-1);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [hasScroll, setHasScroll] = useState(false);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<number | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const initialViewportHeight = useRef<number>(0);

  // Detect touch device
  useEffect(() => {
    const checkTouch = () => {
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
      const noHover = window.matchMedia('(hover: none)').matches;
      
      const isTouchPrimary = hasTouch && isCoarsePointer && noHover;
      setIsTouchDevice(isTouchPrimary);
    };
    checkTouch();
    window.addEventListener('resize', checkTouch);
    return () => window.removeEventListener('resize', checkTouch);
  }, []);

  // Detect virtual keyboard on mobile devices
  useEffect(() => {
    if (!isTouchDevice) return;
    
    // Store initial viewport height
    initialViewportHeight.current = window.visualViewport?.height || window.innerHeight;
    
    const handleViewportChange = () => {
      const currentHeight = window.visualViewport?.height || window.innerHeight;
      const heightDifference = initialViewportHeight.current - currentHeight;
      
      // If viewport height decreased by more than 100px, assume keyboard is open
      // More sensitive detection for better positioning
      setIsKeyboardOpen(heightDifference > 100);
    };
    
    // Use both visual viewport (preferred) and window resize as fallback
    const cleanup: (() => void)[] = [];
    
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
      cleanup.push(() => window.visualViewport?.removeEventListener('resize', handleViewportChange));
    }
    
    // Also listen to window resize as fallback
    window.addEventListener('resize', handleViewportChange);
    cleanup.push(() => window.removeEventListener('resize', handleViewportChange));
    
    // Initial check
    handleViewportChange();
    
    return () => {
      cleanup.forEach(fn => fn());
    };
  }, [isTouchDevice]);

  // Set initial query when component opens
  useEffect(() => {
    if (isOpen && initialQuery !== searchQuery) {
      setSearchQuery(initialQuery);
    }
  }, [isOpen, initialQuery]);

  // Debounced search with 300ms delay
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim().length >= 2) {
      searchTimeoutRef.current = window.setTimeout(async () => {
        const currentQuery = searchQuery;
        setIsSearching(true);
        
        try {
          const results = await nominatimService.searchFormatted(currentQuery, 5);
          
          if (currentQuery === searchQuery) {
            setSearchResults(results);
          }
        } catch (error) {
          console.error('Search error:', error);
          
          if (currentQuery === searchQuery) {
            setSearchResults([]);
          }
        } finally {
          if (currentQuery === searchQuery) {
            setIsSearching(false);
          }
        }
      }, 300);
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Focus the input when search opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
      searchInputRef.current.select(); // Select all text for easy replacement
    }
  }, [isOpen]);

  // Reset state when closing
  useEffect(() => {
    if (!isOpen) {
      setSearchResults([]);
      setSelectedResultIndex(-1);
      setIsSearching(false);
      setHasScroll(false);
    }
  }, [isOpen]);

  // Check for scroll when results change
  useEffect(() => {
    if (resultsRef.current && searchResults.length > 0) {
      const checkScroll = () => {
        const element = resultsRef.current;
        if (element) {
          setHasScroll(element.scrollHeight > element.clientHeight);
        }
      };
      
      // Check after render
      setTimeout(checkScroll, 50);
    } else {
      setHasScroll(false);
    }
  }, [searchResults]);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setSelectedResultIndex(-1);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedResultIndex >= 0 && searchResults[selectedResultIndex]) {
      selectSearchResult(searchResults[selectedResultIndex]);
    } else if (searchResults.length > 0) {
      selectSearchResult(searchResults[0]);
    }
  };

  const selectSearchResult = (result: FormattedSearchResult) => {
    onLocationSelect({
      lat: result.lat,
      lon: result.lon,
      displayName: result.displayName
    });
    
    // Close search and reset
    onClose();
    setSearchQuery('');
    setSearchResults([]);
    setSelectedResultIndex(-1);
  };

  const handleSearchInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedResultIndex(prev => 
        prev < searchResults.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedResultIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedResultIndex >= 0 && searchResults[selectedResultIndex]) {
        selectSearchResult(searchResults[selectedResultIndex]);
      } else if (searchResults.length > 0) {
        selectSearchResult(searchResults[0]);
      }
    }
  };

  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <div 
      className={`location-search-overlay ${className}`}
      style={{ 
        zIndex: 999999, // Inline style to override any Material-UI z-index
        // Dynamic positioning when keyboard is open
        ...(isKeyboardOpen && isTouchDevice ? {
          paddingTop: '5px',
          alignItems: 'flex-start',
          justifyContent: 'center'
        } : {})
      }}
      onClick={onClose} // Handle backdrop clicks to close modal
    >
      <div 
        className={`location-search-container ${hasScroll ? 'has-scroll' : ''}`}
        style={{
          // Dynamic sizing when keyboard is open
          ...(isKeyboardOpen && isTouchDevice ? {
            maxHeight: '30vh',
            transform: 'translateY(0)'
          } : {})
        }}
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on modal content
      >
        {/* Search Input */}
        <div className="location-search-input-container">
          <form onSubmit={handleSearchSubmit}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder={placeholder}
              value={searchQuery}
              onChange={handleSearchInputChange}
              onKeyDown={handleSearchInputKeyDown}
              className="location-search-input"
              onClick={(e) => e.stopPropagation()}
            />
          </form>
          
          {/* Close button */}
          <button
            className="location-search-close"
            onClick={onClose}
            aria-label="Close search"
          >
            <svg viewBox="0 0 24 24" fill="none">
              <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Search Results */}
        {(searchResults.length > 0 || isSearching) && (
          <div 
            className="location-search-results" 
            ref={resultsRef}
            style={{
              // Dynamic sizing when keyboard is open
              ...(isKeyboardOpen && isTouchDevice ? {
                maxHeight: 'calc(30vh - 55px)'
              } : {})
            }}
          >
            {isSearching && (
              <div className="location-search-loading">
                <div className="location-search-loading-spinner" />
                Suche läuft...
              </div>
            )}
            
            {searchResults.map((result, index) => (
              <div
                key={result.id}
                className={`location-search-result-item ${result.subLine ? 'two-line' : 'single-line'} ${
                  !isTouchDevice && index === selectedResultIndex ? 'selected' : ''
                }`}
                onMouseEnter={() => {
                  if (!isTouchDevice) {
                    setSelectedResultIndex(index);
                  }
                }}
                onMouseLeave={() => {
                  if (!isTouchDevice) {
                    setSelectedResultIndex(-1);
                  }
                }}
                onClick={() => selectSearchResult(result)}
              >
                {/* Location Icon */}
                <span className={`location-search-result-icon ${result.subLine ? 'two-line' : ''}`}>
                  {result.icon}
                </span>
                
                {/* Location Name - Two Lines */}
                <div
                  className={`location-search-result-text ${result.subLine ? 'two-line' : ''}`}
                  title={result.displayName}
                >
                  {/* Primary line (name/street) */}
                  <span className="location-search-result-main">
                    {result.mainLine}
                  </span>
                  
                  {/* Secondary line (address/context) */}
                  {result.subLine && (
                    <span className="location-search-result-sub">
                      {result.subLine}
                    </span>
                  )}
                </div>
                
                {/* Country Flag */}
                <span className="location-search-result-flag" title="Country">
                  {result.countryFlag}
                </span>
              </div>
            ))}
            
            {/* Scroll indicator for many results */}
            {hasScroll && searchResults.length > 3 && (
              <div className="location-search-scroll-hint">
                <span>↕ Scrollen für weitere Ergebnisse</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Render in a portal to the document body to avoid z-index issues
  return createPortal(modalContent, document.body);
};
